/**
 * Tag 目录类型定义
 *
 * 定义“个体特征”作为人物基础模型中的语义差异层。
 *
 * 当前口径：
 * - 本文件只负责结构定义（Schema / Type / 分类顺序）。
 * - 真正的 tag 真相源位于 `modules/tags/data/registry.ts`。
 * - 词汇归一化、维度聚合、提示词词表等都应从注册表派生，而不是另存第二份手写表。
 *
 * 设计目标：
 * - 承载 tag 的组合规则（通用 / 互斥 / 多选一 / 依赖），避免实例化时随意叠加。
 * - 承载 tag 对属性、技能池、技能阶级的修正，让 tag 真正成为个体差异源。
 * - 承载 tag 的别名与词库可见性，让“对外一个 tags 字段、对内一个 registry”成立。
 */
import { z } from 'zod';

// ============================================
// 标签分类
// ============================================

/**
 * 标签一级分类键。
 *
 * 说明：
 * - 这些分类用于 registry 聚合、词表投影与提示词展示。
 * - 对外输入仍然只有 `profile.tags` 一个字段；分类只属于系统内部解释层。
 */
export const TAG_CATEGORY_KEYS = [
  'entity_kind',
  'race',
  'identity',
  'combat_role',
  'combat_style',
  'weapon_affinity',
  'magic_affinity',
  'temperament',
  'environment',
  'skill_domain',
  'effect_intent',
  'target_shape',
  'resource_profile',
  'tempo',
  'status_theme',
  'synergy_hint',
] as const;

export const TagCategoryKeySchema = z.enum(TAG_CATEGORY_KEYS);
export type TagCategoryKey = z.infer<typeof TagCategoryKeySchema>;

/**
 * 标签维度条目。
 *
 * 由 registry 自动聚合后提供给提示词与测试使用。
 */
export interface TagCatalogDimension {
  key: TagCategoryKey;
  tags: readonly string[];
}

// ============================================
// 治理字段
// ============================================

/**
 * 标签治理状态：
 * - candidate：候选，允许保留但默认不进入 AI 主词表
 * - promoted：正式标签
 * - disabled：停用标签，保留历史兼容但不再对外使用
 */
export const TagStatusSchema = z.enum(['candidate', 'promoted', 'disabled']);
export type TagStatus = z.infer<typeof TagStatusSchema>;

// ============================================
// 元规则
// ============================================

/**
 * Tag 组合类型
 *
 * - standalone：可与任何其它 tag 共存（典型如战斗风格、亲和元素）。
 * - one-of：同 group 下最多选一个（典型如年龄段：幼年 / 青年 / 中年 / 老年）。
 * - mutex：与列表中的 tag 互斥（典型如"狂战士" vs "潜行专精"）。
 *
 * 注意：one-of 与 mutex 的差别在语义：
 * - one-of 描述一个枚举维度上必然只能选其一。
 * - mutex 描述两个 tag 之间的概念冲突，但允许其中一方与外部其它 tag 自由组合。
 */
export const TagKindSchema = z.enum(['standalone', 'one-of', 'mutex']);
export type TagKind = z.infer<typeof TagKindSchema>;

// ============================================
// 数值修正
// ============================================

/**
 * 属性修正：tag 对六维属性的加减值
 *
 * key 期望取自 ATTRIBUTE_KEYS，但运行时与现有 AttributeBonusesSchema 一致采用
 * `z.record(z.string(), ...)` 形式，便于与外部 JSON 数据互通且支持空默认。
 */
export const TagAttributeModifiersSchema = z.record(z.string(), z.number());
export type TagAttributeModifiers = z.infer<typeof TagAttributeModifiersSchema>;

// ============================================
// Tag 目录条目
// ============================================

/**
 * Tag 目录条目（统一重构后的完整定义）
 */
export const TagDefinitionSchema = z.object({
  /** Tag ID（中文 canonical 形式，与现有公开标签口径对齐） */
  id: z.string().min(1),
  /** 显示名（默认与 id 相同） */
  label: z.string().default(''),
  /** 同义词 / 英文 / 历史写法别名，统一映射到 id */
  aliases: z.array(z.string()).default([]),
  /** 所属维度；候选标签可暂不分类 */
  category: TagCategoryKeySchema.optional(),
  /** 极简说明，用于后续提示词和管理面板扩展 */
  description: z.string().default(''),
  /** 词表投影维度；缺省时等同于 category，允许同一 canonical tag 同时出现在多个维度中 */
  catalogCategories: z.array(TagCategoryKeySchema).default([]),
  /** 是否对外暴露到 canonical 词表与提示词维度中 */
  visibleInCatalog: z.boolean().default(true),
  /** 标签治理状态 */
  status: TagStatusSchema.default('promoted'),
  /** 是否允许暴露给 AI 作为候选词 */
  exposeToAi: z.boolean().default(true),

  // ========== 组合规则 ==========
  /** 组合类型 */
  kind: TagKindSchema.default('standalone'),
  /** one-of / mutex 的同组标识；同组下的 tag 视为同一维度 */
  group: z.string().optional(),
  /** 显式互斥 tag id 列表 */
  incompatibleWith: z.array(z.string()).default([]),
  /** 前置依赖 tag id 列表（如"龙裔"要求同时拥有"龙族"先天 tag） */
  requires: z.array(z.string()).default([]),
  /** 仅适用于这些 raceId（留空表示对所有种族通用） */
  raceWhitelist: z.array(z.string()).default([]),

  // ========== 数值修正 ==========
  /** 属性修正 */
  attributeModifiers: TagAttributeModifiersSchema.default({}),
  /** 扩展技能池（叠加到种族 / 构筑模板的池上） */
  skillPoolBoost: z.array(z.string()).default([]),
  /** 抽取技能时的阶级偏置（-2 ~ +2） */
  skillTierBias: z.number().min(-2).max(2).default(0),
});
export type TagDefinition = z.infer<typeof TagDefinitionSchema>;
